import { create } from 'zustand';

export const PREVIEW_STORAGE_KEY = 'kp_preview';

interface PersistedPreview {
  batchId: string;
  batchName?: string;
  returnTo: string;
}

interface PreviewStore {
  active: boolean;
  batchId: string | null;
  batchName: string | null;
  returnTo: string | null;

  enterPreview: (opts: PersistedPreview) => void;
  exitPreview: () => void;
  rehydrate: () => void;
}

function readPersisted(): PersistedPreview | null {
  try {
    const raw = sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.batchId !== 'string' || typeof parsed?.returnTo !== 'string') return null;
    return {
      batchId: parsed.batchId,
      batchName: typeof parsed.batchName === 'string' ? parsed.batchName : undefined,
      returnTo: parsed.returnTo,
    };
  } catch {
    return null;
  }
}

export const usePreviewStore = create<PreviewStore>((set) => ({
  active: false,
  batchId: null,
  batchName: null,
  returnTo: null,

  enterPreview: ({ batchId, batchName, returnTo }) => {
    sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify({ batchId, batchName, returnTo }));
    set({ active: true, batchId, batchName: batchName ?? null, returnTo });
  },

  exitPreview: () => {
    sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    set({ active: false, batchId: null, batchName: null, returnTo: null });
  },

  rehydrate: () => {
    const persisted = readPersisted();
    if (persisted) {
      set({
        active: true,
        batchId: persisted.batchId,
        batchName: persisted.batchName ?? null,
        returnTo: persisted.returnTo,
      });
    }
  },
}));

// Survive a page refresh: restore any persisted preview at module load.
usePreviewStore.getState().rehydrate();
